import React, { useMemo } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import BaseButton from '../form/BaseButton.jsx';
import BaseOverlay from '../layout/BaseOverlay.jsx';
import IconCancel from '../images/IconCancel.jsx';
import { useGetFileCountriesQuery } from '../../api/misc.js';

const AdvancedSearchFilterFiles = ({ filters, setFilters, visible, setVisible, applyFilters }) => {
    const hideDialog = () => {
        setVisible(false);
    };

    const changeFilter = (field, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: value
        }));
    };

    const resetFilterField = (field) => {
        changeFilter(field, Array.isArray(filters[field]) ? [] : null);
    };

    const filter = () => {
        applyFilters();
        hideDialog();
    };

    const { data: countriesData } = useGetFileCountriesQuery({});
    const countriesList = useMemo(() => {
        if (countriesData?.response?.file_countries) {
            return countriesData.response.file_countries.map((country) => ({ label: country.name, value: country.id }));
        }

        return [];
    }, [countriesData]);

    return (
        <BaseOverlay show={visible} className='overflow-auto h-[100dvh] rounded-none'>
            <div className='min-h-96 max-w-3xl w-full mx-auto p-4'>
                <div className='flex justify-end py-4'>
                    <div className='text-theme1-primary font-bold cursor-pointer' onClick={hideDialog}>
                        Close
                    </div>
                </div>
                <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>Filter</div>
                <div className='text-left'>
                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>File Types</h2>
                        <div className='flex items-center gap-2'>
                            <MultiSelect
                                value={filters.types}
                                onChange={(e) => changeFilter('types', e.value)}
                                options={[
                                    { label: 'Media', value: 'media' },
                                    { label: 'Document', value: 'document' },
                                    { label: 'Spreadsheet', value: 'spreadsheet' }
                                ]}
                                optionLabel='label'
                                placeholder='Select file types'
                                maxSelectedLabels={3}
                                className={`bg-transparent border-theme1-text3 border rounded-4xl px-2 ${
                                    filters?.types?.length > 0 ? 'w-[90%]' : 'w-full'
                                }`}
                            />
                            {filters?.types?.length > 0 && (
                                <div
                                    className='cursor-pointer w-[36px] flex-shrink-0'
                                    onClick={() => resetFilterField('types')}
                                >
                                    <IconCancel className='fill-theme1-primary' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Country</h2>
                        <div className='flex items-center'>
                            <MultiSelect
                                value={filters.countries}
                                onChange={(e) => changeFilter('countries', e.value)}
                                options={countriesList}
                                optionLabel='label'
                                filter
                                placeholder={'Select countries'}
                                maxSelectedLabels={3}
                                className={`bg-transparent border-theme1-text3 border rounded-3xl px-2 ${
                                    filters.countries.length > 0 ? 'w-[90%]' : 'w-full'
                                }`}
                            />

                            {filters.countries.length > 0 && (
                                <div
                                    className='cursor-pointer w-[36px] flex-shrink-0'
                                    onClick={() => changeFilter('countries', [])}
                                >
                                    <IconCancel className='fill-theme1-primary' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='mb-[30px]'>
                        <h2 className='pb-[10px] text-xl font-bold'>Date Range</h2>
                        <div className='flex items-center gap-2'>
                            <Calendar
                                value={filters.dateRange}
                                onChange={(e) => changeFilter('dateRange', e.value)}
                                selectionMode='range'
                                readOnlyInput
                                hideOnRangeSelection
                                dateFormat='dd/mm/yy'
                                className={`border-theme1-text3 border rounded-4xl px-5 py-3 ${
                                    filters?.dateRange?.length > 0 ? 'w-[90%]' : 'w-full'
                                }`}
                            />
                            {filters?.dateRange?.length > 0 && (
                                <div
                                    className='cursor-pointer w-[36px] flex-shrink-0'
                                    onClick={() => resetFilterField('dateRange')}
                                >
                                    <IconCancel className='fill-theme1-primary' />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='flex justify-between gap-4 w-full'>
                        <BaseButton
                            className='bg-theme1-primary text-theme1-white flex-shrink-0'
                            textValue='Apply Filters'
                            onClick={filter}
                        />
                    </div>
                </div>
            </div>
        </BaseOverlay>
    );
};

export default React.memo(AdvancedSearchFilterFiles);
